import React, { FC } from 'react';
import { connect } from 'react-redux';
import hash from 'object-hash';
import { selectedRouteAction, toggleLegend } from '@store/actions';
import bus from '@assets/bus.svg';
import shuttles from '@assets/minivan.svg';
import trams from '@assets/tramway.svg';
import trolleys from '@assets/trolleybus.svg';
import walking from '@assets/walking.svg';
import MobileShowMapBtn from '../MobileShowMapBtn';
import st from './Undergrounds.module.css';
import { structuredSelector } from './structuredSelector';

const Arrow = () => <div className={st.routeArrow} />;

const RouteText = ({ route }) => {
  if (route.type === 'walkway') {
    return (
      <>
        <img className={st.routePartIcon} src={walking} alt={route.type} />
        <span className={st.routePartDescription}>{route.duration}</span>
      </>
    );
  }

  if (route.kind === 'custom') {
    const iconsMap = {
      bus,
      shuttles,
      trams,
      trolleys,
    };
    return (
      <div className={st.customRoute}>
        <img
          className={st.routePartCustomIcon}
          src={iconsMap[route.type]}
          alt={route.type}
        />
        <span className={st.routePartDescription}>{route.text}</span>
      </div>
    );
  }

  return (
    <>
      <img className={st.routePartIcon} src={bus} alt={route.type} />
      <span className={st.routePartDescription}>
        {route.routesName.map((item, index) => {
          if (index + 1 < route.routesName.length) {
            return `${item}, `;
          }
          return `${item} `;
        })}
        {route.navigationTo}
      </span>
    </>
  );
};

type RouteProps = {
  route: any;
};

const Route: FC<RouteProps> = ({ route }) => {
  if (!route?.selected) {
    return (
      <button type="button" className={st.routeDistance}>
        {route.total_distance}
      </button>
    );
  }

  const routeText = route.routeText.filter(
    (route) =>
      route.type === 'walkway' ||
      (route.type !== 'crossing' && route.routesName)
  );
  return (
    <>
      <button
        type="button"
        className={st.routeDistanceSelected}
        // onClick={() => handleSelect(route.routeId)}
      >
        {route.total_distance}
      </button>

      <div className={st.routeWrapper}>
        <p className={st.routeDescription}>{route.name}</p>
        <div className={st.routeParts}>
          {routeText.map((part, index) => (
            <React.Fragment key={hash(part)}>
              <div className={st.routePart}>
                <RouteText route={part} />
                <hr />
              </div>
              {index + 1 < routeText.length && <Arrow />}
            </React.Fragment>
          ))}
        </div>
      </div>
    </>
  );
};

const RouteRow = ({ isDetailed, route, handleSelectedRoute }) => (
  <div className={isDetailed ? st.undergroundItemDetailed : st.undergroundItem}>
    <div className={st.undergroundMarkers}>
      {route.colors &&
        route.colors.map((color) => (
          <div
            key={color}
            className={
              isDetailed ? st.undergroundMarkerWithIcon : st.undergroundMarker
            }
            style={{ backgroundColor: color }}
          />
        ))}
    </div>
    <div
      className={
        route.selected ? st.undergroundNameSelected : st.undergroundName
      }
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
      <div onClick={() => handleSelectedRoute(route.routeId)}>
        {route.startPointName}
        {isDetailed ? (
          <Route route={route} />
        ) : (
          <span
            className={
              route.selected ? st.undergroundTimeSelected : st.undergroundTime
            }
          >
            {route.ui_total_duration}
            .
            {route.transfer_comment}
          </span>
        )}
      </div>
      {route?.selected && <MobileShowMapBtn className="fromRoute" isDetailed />}
    </div>
  </div>
);

type PlainRoutesProps = {
  address: AddressTypeProps;
  hidden: boolean;
};

const PlainRoutes: FC<PlainRoutesProps> = ({ address, hidden }) => {
  if (
    hidden ||
    !(address.buses || address.shuttles || address.trams || address.trolleys)
  ) {
    return null;
  }

  return (
    <div className={st.customContainer}>
      {address.buses && (
        <RouteText
          route={{
            kind: 'custom',
            type: 'bus',
            text: address.buses,
          }}
        />
      )}
      {address.shuttles && (
        <RouteText
          route={{
            kind: 'custom',
            type: 'shuttles',
            text: address.shuttles,
          }}
        />
      )}
      {address.trams && (
        <RouteText
          route={{
            kind: 'custom',
            type: 'trams',
            text: address.trams,
          }}
        />
      )}
      {address.trolleys && (
        <RouteText
          route={{
            kind: 'custom',
            type: 'trolleys',
            text: address.trolleys,
          }}
        />
      )}
    </div>
  );
};

type UndergroundsProps = {
  isDetailed?: boolean;
  handleSelectedRoute: () => void;
  routes: any;
  isRoutesFetching: boolean;
  address: AddressTypeProps;
};

const Undergrounds: FC<UndergroundsProps> = ({
  isDetailed,
  routes,
  isRoutesFetching,
  handleSelectedRoute,
  address,
}) => {
  const isShortDistance = address.metroLocations.some(
    (location) => location.distance <= 5000
  );
  const isExistPlainRoutes =
    address.buses || address.shuttles || address.trams || address.trolleys;
  const isExistMapRoutes = isShortDistance && routes?.length > 0;
  const isRoutesExist = isExistMapRoutes || isExistPlainRoutes;

  return isRoutesExist ? (
    <>
      {isDetailed && <h2 className={st.title}>Как добраться</h2>}

      <div className={st.undergroundItems} data-testid="undergroundsChoose">
        {isExistMapRoutes &&
          routes.map((route) => (
            <RouteRow
              key={hash(route)}
              isDetailed={isDetailed}
              route={route}
              handleSelectedRoute={handleSelectedRoute}
            />
          ))}

        <PlainRoutes
          address={address}
          hidden={isExistMapRoutes || isRoutesFetching || !isDetailed}
        />
      </div>
    </>
  ) : null;
};

const mapDispatchToProps = (dispatch) => ({
  toggleLegend: () => dispatch(toggleLegend()),
  handleSelectedRoute: (payload) => dispatch(selectedRouteAction(payload)),
});

// @ts-ignore
export default connect(structuredSelector, mapDispatchToProps)(Undergrounds);
